import { Box, Divider, Radio, Stack, Typography } from "@mui/material";
import { ActionButton, Flex, LabelValue, selectableOptionSx } from "@zfp-akademie-a-s/zfp-design";
import { CheckCircleIcon, InfoIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import type { CaseInvestmentStrategy } from "Infrastructure/ApiZfp/Schema";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useId } from "react";
import { formatPercent, formatSrriSri } from "./utils";

type Props = {
  readonly portfolio: CaseInvestmentStrategy;
  readonly selected: boolean;
  readonly onClick: () => void;
  readonly disabled?: boolean;
};

// A portfolio to pick, as the package's answer of its own shape: a <label> around a Radio, styled by
// selectableOptionSx, which reads the chosen state from the Radio. The RadioGroup is around the grid.
// The Radio shows only its check, on the right of the chosen card after the info button, which stays
// either way.
export const InvestmentsNsurePortfolioCard = ({ portfolio, selected, onClick, disabled = false }: Props) => {
  const { t, resolveResourcePath } = useResource();
  const nameId = useId();
  const inputId = useId();

  return (
    <Box
      component="label"
      // the info button comes first and is labelable too, so the label names its Radio outright
      htmlFor={inputId}
      sx={[
        selectableOptionSx,
        {
          display: "block",
          height: "100%",
          boxSizing: "border-box",
          px: Spacing.XL,
          py: Spacing.L,
          borderColor: "divider",
          // SelectableOption's radius, as the card is one
          borderRadius: Radius.M,
        },
        disabled && { opacity: 0.5 },
      ]}
    >
      <Stack spacing={Spacing.M}>
        <Box>
          <Flex justifyContent="space-between" alignItems="center" gap={Spacing.S} minHeight="32px">
            <Typography variant="body1" id={nameId} fontWeight={700}>
              {portfolio.name}
            </Typography>
            <Flex alignItems="center" flexShrink={0}>
              {/* a button inside the label doesn't choose the card, so it needs no click handler */}
              {portfolio.description && (
                <ActionButton
                  label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Description)}
                  description={portfolio.description}
                  icon={<InfoIcon />}
                />
              )}
              {/* named by the portfolio's name alone, not the whole card */}
              <Radio
                size="small"
                checked={selected}
                onChange={onClick}
                disabled={disabled}
                icon={<Box component="span" />}
                checkedIcon={<CheckCircleIcon />}
                slotProps={{ input: { id: inputId, "aria-labelledby": nameId } }}
                disableRipple
                // the check fills a 32px slot like the info button with its 18px icon; the card, not the Radio, is the hover target
                sx={{ p: selected ? "7px" : 0, "& .MuiSvgIcon-root": { fontSize: 18 }, "&:hover": { bgcolor: "transparent" } }}
              />
            </Flex>
          </Flex>
          <Typography variant="caption" color="text.secondary">
            {t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.PortfolioCard.Category)}{" "}
            {t(resolveResourcePath(`Resources.AnalysisDetail.GoalsFulfillment.Nsure.StrategyRiskType.${portfolio.strategyRiskType}`))}
          </Typography>
        </Box>
        <Divider />
        {/* the last column as wide as its label, "Očekáv. zhodnocení" on one line; the other two share the rest */}
        <Box display="grid" gridTemplateColumns="1fr 1fr auto" columnGap={Spacing.L}>
          <LabelValue
            uppercase={false}
            label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.SrriSri)}
            value={formatSrriSri(portfolio.srri, portfolio.sri)}
          />
          <LabelValue
            uppercase={false}
            label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.Esg)}
            value={formatPercent(portfolio.esg)}
          />
          <LabelValue
            uppercase={false}
            label={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Labels.ExpectedPerformance)}
            value={formatPercent(portfolio.expectedPerformance)}
          />
        </Box>
      </Stack>
    </Box>
  );
};
